import { useTranslation } from 'react-i18next';

import { StatCard } from '@nucleus-commerce/admin-kit/layout';
import type { AdminAuction } from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';

import { extensionMs } from '../schemas/auction-rules';

import { AuctionCountdown } from './auction-countdown';

function TimeCard({ auction }: { auction: AdminAuction }) {
  const { t } = useTranslation('auctions');
  const format = useFormat();
  const extended = extensionMs(auction);
  const extendedText =
    extended > 0
      ? t('summary.extended', {
          minutes: Math.round(extended / 60_000),
          original: format.dateTime(auction.originalEndsAt),
        })
      : undefined;

  if (auction.status === 'live') {
    return (
      <StatCard
        label={t('summary.timeLeft')}
        value={<AuctionCountdown target={auction.endsAt} />}
        detail={extendedText ?? t('summary.endsAt', { when: format.dateTime(auction.endsAt) })}
        tone={extended > 0 ? 'warning' : 'neutral'}
        tour="auctions.detail.time-left"
      />
    );
  }
  if (auction.status === 'scheduled' || auction.status === 'draft') {
    return (
      <StatCard
        label={t('summary.startsIn')}
        value={<AuctionCountdown target={auction.startsAt} />}
        detail={t('summary.startsAt', { when: format.dateTime(auction.startsAt) })}
        tour="auctions.detail.time-left"
      />
    );
  }
  const ended = auction.endedAt ?? auction.cancelledAt ?? auction.endsAt;
  return (
    <StatCard
      label={auction.status === 'cancelled' ? t('summary.cancelled') : t('summary.ended')}
      value={<span className="text-xl">{format.date(ended)}</span>}
      detail={extendedText ?? format.time(ended)}
      tour="auctions.detail.time-left"
    />
  );
}

/** The numbers staff watch while bidding runs; the page polls, so they move on their own. */
export function AuctionSummary({ auction }: { auction: AdminAuction }) {
  const { t } = useTranslation('auctions');
  const format = useFormat();
  const price = auction.hammerPrice ?? auction.currentPrice;
  return (
    <div
      className="grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-7"
      data-tour="auctions.detail.summary"
      data-testid="auction-summary"
    >
      <StatCard
        label={auction.hammerPrice ? t('summary.hammer') : t('summary.currentPrice')}
        value={price ? format.money(price) : t('none')}
        detail={t('summary.startingAt', { price: format.money(auction.startingPrice) })}
      />
      <StatCard
        label={t('summary.nextMinimum')}
        value={auction.status === 'live' ? format.money(auction.nextMinimumBid) : t('none')}
      />
      <StatCard label={t('summary.bids')} value={format.number(auction.bidCount)} />
      <StatCard label={t('summary.bidders')} value={format.number(auction.bidderCount)} />
      <StatCard
        label={t('summary.watchers')}
        value={format.number(auction.watcherCount)}
        detail={
          auction.buyNowPrice
            ? auction.buyNowAvailable
              ? t('summary.buyNowOpen', { price: format.money(auction.buyNowPrice) })
              : t('summary.buyNowClosed')
            : undefined
        }
      />
      <StatCard
        label={t('summary.reserve')}
        value={
          auction.reservePrice === null
            ? t('reserve.none')
            : auction.reserveMet
              ? t('reserve.met')
              : t('reserve.notMet')
        }
        detail={
          auction.reservePrice
            ? t('summary.reserveAmount', { price: format.money(auction.reservePrice) })
            : undefined
        }
        tone={auction.reservePrice && auction.reserveMet ? 'positive' : 'neutral'}
      />
      <TimeCard auction={auction} />
    </div>
  );
}
